/**
 * ====================================================================
 * FIKO GD&T SYMBOL RENDERING SYSTEM
 * ====================================================================
 *
 * ASME Y14.5-2018 ve ISO 1101:2017 uyumlu profesyonel sembol çizimi.
 * Canvas 2D API kullanarak yüksek kaliteli vektörel sembol rendering.
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import {
    GDTCharacteristicType,
    MaterialCondition,
    ToleranceZoneModifier,
    TOLERANCE_ZONE_SYMBOLS
} from './GDTTypes';

// ====================================================================
// SEMBOL ÇİZİM KONFİGÜRASYONU
// ====================================================================

/**
 * Sembol çizim boyutu
 */
export interface SymbolDrawConfig {
    /** Sembol boyutu (piksel) */
    size: number;

    /** Çizgi kalınlığı */
    lineWidth: number;

    /** Renk */
    color: string;

    /** Arka plan rengi (opsiyonel) */
    backgroundColor?: string;

    /** Padding */
    padding?: number;
}

/**
 * Varsayılan çizim konfigürasyonu
 */
const DEFAULT_DRAW_CONFIG: SymbolDrawConfig = {
    size: 24,
    lineWidth: 2,
    color: '#FFFFFF',
    backgroundColor: 'transparent',
    padding: 2
};

// ====================================================================
// CANVAS CONTEXT YARDIMCI FONKSİYONLAR
// ====================================================================

/**
 * Temiz bir canvas context oluştur
 */
function createCleanContext(
    canvas: HTMLCanvasElement,
    width: number,
    height: number,
    scale: number = 2
): CanvasRenderingContext2D {
    canvas.width = width * scale;
    canvas.height = height * scale;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) throw new Error('Failed to get 2D context');

    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.clearRect(0, 0, width, height);

    return ctx;
}

/**
 * Context ayarlarını uygula
 */
function applyContextSettings(
    ctx: CanvasRenderingContext2D,
    config: SymbolDrawConfig
): void {
    ctx.strokeStyle = config.color;
    ctx.fillStyle = config.color;
    ctx.lineWidth = config.lineWidth;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
}

// ====================================================================
// GD&T SEMBOL ÇİZİM FONKSİYONLARI
// ====================================================================

/**
 * Ana sembol çizim fonksiyonu
 */
export function drawGDTSymbol(
    ctx: CanvasRenderingContext2D,
    type: GDTCharacteristicType,
    x: number,
    y: number,
    config: Partial<SymbolDrawConfig> = {}
): void {
    const cfg = { ...DEFAULT_DRAW_CONFIG, ...config };
    applyContextSettings(ctx, cfg);

    const size = cfg.size;
    const cx = x + size / 2;
    const cy = y + size / 2;
    const r = (size - cfg.padding * 2) / 2;

    switch (type) {
        case GDTCharacteristicType.FLATNESS:
            drawFlatness(ctx, cx, cy, r, cfg);
            break;
        case GDTCharacteristicType.STRAIGHTNESS:
            drawStraightness(ctx, cx, cy, r, cfg);
            break;
        case GDTCharacteristicType.CIRCULARITY:
            drawCircularity(ctx, cx, cy, r, cfg);
            break;
        case GDTCharacteristicType.CYLINDRICITY:
            drawCylindricity(ctx, cx, cy, r, cfg);
            break;
        case GDTCharacteristicType.PROFILE_OF_LINE:
            drawProfileOfLine(ctx, cx, cy, r, cfg);
            break;
        case GDTCharacteristicType.PROFILE_OF_SURFACE:
            drawProfileOfSurface(ctx, cx, cy, r, cfg);
            break;
        case GDTCharacteristicType.ANGULARITY:
            drawAngularity(ctx, cx, cy, r, cfg);
            break;
        case GDTCharacteristicType.PERPENDICULARITY:
            drawPerpendicularity(ctx, cx, cy, r, cfg);
            break;
        case GDTCharacteristicType.PARALLELISM:
            drawParallelism(ctx, cx, cy, r, cfg);
            break;
        case GDTCharacteristicType.POSITION:
            drawPosition(ctx, cx, cy, r, cfg);
            break;
        case GDTCharacteristicType.CONCENTRICITY:
            drawConcentricity(ctx, cx, cy, r, cfg);
            break;
        case GDTCharacteristicType.SYMMETRY:
            drawSymmetry(ctx, cx, cy, r, cfg);
            break;
        case GDTCharacteristicType.CIRCULAR_RUNOUT:
            drawCircularRunout(ctx, cx, cy, r, cfg);
            break;
        case GDTCharacteristicType.TOTAL_RUNOUT:
            drawTotalRunout(ctx, cx, cy, r, cfg);
            break;
    }
}

// ═══════════════════════════════════════════════════════════════
// FORM TOLERANCES
// ═══════════════════════════════════════════════════════════════

/**
 * Düzlük (Flatness) - İki paralel çizgi
 */
function drawFlatness(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    cfg: SymbolDrawConfig
): void {
    const halfWidth = r * 0.8;
    const gap = r * 0.3;

    ctx.beginPath();
    // Üst çizgi
    ctx.moveTo(cx - halfWidth, cy - gap);
    ctx.lineTo(cx + halfWidth, cy - gap);
    // Alt çizgi
    ctx.moveTo(cx - halfWidth, cy + gap);
    ctx.lineTo(cx + halfWidth, cy + gap);
    ctx.stroke();
}

/**
 * Doğruluk (Straightness) - Tek yatay çizgi
 */
function drawStraightness(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    cfg: SymbolDrawConfig
): void {
    const halfWidth = r * 0.8;

    ctx.beginPath();
    ctx.moveTo(cx - halfWidth, cy);
    ctx.lineTo(cx + halfWidth, cy);
    ctx.stroke();
}

/**
 * Dairesellik (Circularity) - Daire
 */
function drawCircularity(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    cfg: SymbolDrawConfig
): void {
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.6, 0, Math.PI * 2);
    ctx.stroke();
}

/**
 * Silindiriklik (Cylindricity) - Daire + üst/alt çizgiler
 */
function drawCylindricity(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    cfg: SymbolDrawConfig
): void {
    const circleR = r * 0.5;
    const lineOffset = r * 0.75;

    // Daire
    ctx.beginPath();
    ctx.arc(cx, cy, circleR, 0, Math.PI * 2);
    ctx.stroke();

    // Üst çizgi
    ctx.beginPath();
    ctx.moveTo(cx - circleR, cy - lineOffset);
    ctx.lineTo(cx + circleR, cy - lineOffset);
    ctx.stroke();

    // Alt çizgi
    ctx.beginPath();
    ctx.moveTo(cx - circleR, cy + lineOffset);
    ctx.lineTo(cx + circleR, cy + lineOffset);
    ctx.stroke();
}

// ═══════════════════════════════════════════════════════════════
// PROFILE TOLERANCES
// ═══════════════════════════════════════════════════════════════

/**
 * Çizgi Profili (Profile of a Line) - Yarım daire (alt)
 */
function drawProfileOfLine(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    cfg: SymbolDrawConfig
): void {
    ctx.beginPath();
    ctx.arc(cx, cy - r * 0.2, r * 0.6, 0, Math.PI);
    ctx.stroke();
}

/**
 * Yüzey Profili (Profile of a Surface) - Yarım daire (alt) + alt çizgi
 */
function drawProfileOfSurface(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    cfg: SymbolDrawConfig
): void {
    const arcR = r * 0.5;
    const arcY = cy - r * 0.2;

    // Yarım daire
    ctx.beginPath();
    ctx.arc(cx, arcY, arcR, 0, Math.PI);
    ctx.stroke();

    // Alt çizgi
    ctx.beginPath();
    ctx.moveTo(cx - arcR, arcY + arcR + r * 0.2);
    ctx.lineTo(cx + arcR, arcY + arcR + r * 0.2);
    ctx.stroke();
}

// ═══════════════════════════════════════════════════════════════
// ORIENTATION TOLERANCES
// ═══════════════════════════════════════════════════════════════

/**
 * Açısallık (Angularity) - Açı sembolü
 */
function drawAngularity(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    cfg: SymbolDrawConfig
): void {
    const size = r * 0.7;

    ctx.beginPath();
    ctx.moveTo(cx - size, cy + size * 0.5);
    ctx.lineTo(cx, cy - size * 0.5);
    ctx.lineTo(cx + size, cy + size * 0.5);
    ctx.stroke();
}

/**
 * Diklik (Perpendicularity) - ⊥ sembolü
 */
function drawPerpendicularity(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    cfg: SymbolDrawConfig
): void {
    const size = r * 0.7;

    ctx.beginPath();
    // Yatay çizgi
    ctx.moveTo(cx - size, cy + size * 0.5);
    ctx.lineTo(cx + size, cy + size * 0.5);
    // Dikey çizgi
    ctx.moveTo(cx, cy + size * 0.5);
    ctx.lineTo(cx, cy - size * 0.5);
    ctx.stroke();
}

/**
 * Paralellik (Parallelism) - İki paralel eğik çizgi
 */
function drawParallelism(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    cfg: SymbolDrawConfig
): void {
    const size = r * 0.6;
    const gap = r * 0.25;

    ctx.beginPath();
    // Sol çizgi
    ctx.moveTo(cx - gap, cy - size);
    ctx.lineTo(cx - gap, cy + size);
    // Sağ çizgi
    ctx.moveTo(cx + gap, cy - size);
    ctx.lineTo(cx + gap, cy + size);
    ctx.stroke();
}

// ═══════════════════════════════════════════════════════════════
// LOCATION TOLERANCES
// ═══════════════════════════════════════════════════════════════

/**
 * Konum (Position) - Çarpı daire içinde (crosshair)
 */
function drawPosition(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    cfg: SymbolDrawConfig
): void {
    const circleR = r * 0.6;
    const crossSize = r * 0.4;

    // Dış daire
    ctx.beginPath();
    ctx.arc(cx, cy, circleR, 0, Math.PI * 2);
    ctx.stroke();

    // Çarpı
    ctx.beginPath();
    ctx.moveTo(cx - crossSize, cy);
    ctx.lineTo(cx + crossSize, cy);
    ctx.moveTo(cx, cy - crossSize);
    ctx.lineTo(cx, cy + crossSize);
    ctx.stroke();
}

/**
 * Eş Merkezlilik (Concentricity) - İç içe iki daire [DEPRECATED]
 */
function drawConcentricity(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    cfg: SymbolDrawConfig
): void {
    // Dış daire
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.7, 0, Math.PI * 2);
    ctx.stroke();

    // İç daire
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.35, 0, Math.PI * 2);
    ctx.stroke();
}

/**
 * Simetri (Symmetry) - Üç yatay çizgi [DEPRECATED]
 */
function drawSymmetry(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    cfg: SymbolDrawConfig
): void {
    const width = r * 0.7;
    const gap = r * 0.35;

    ctx.beginPath();
    // Üst çizgi
    ctx.moveTo(cx - width, cy - gap);
    ctx.lineTo(cx + width, cy - gap);
    // Orta çizgi
    ctx.moveTo(cx - width, cy);
    ctx.lineTo(cx + width, cy);
    // Alt çizgi
    ctx.moveTo(cx - width, cy + gap);
    ctx.lineTo(cx + width, cy + gap);
    ctx.stroke();
}

// ═══════════════════════════════════════════════════════════════
// RUNOUT TOLERANCES
// ═══════════════════════════════════════════════════════════════

/**
 * Dairesel Kaçıklık (Circular Runout) - Tek ok
 */
function drawCircularRunout(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    cfg: SymbolDrawConfig
): void {
    const size = r * 0.7;

    // Çizgi
    ctx.beginPath();
    ctx.moveTo(cx - size * 0.8, cy + size * 0.5);
    ctx.lineTo(cx + size * 0.5, cy - size * 0.5);
    ctx.stroke();

    // Ok başı
    const arrowSize = r * 0.25;
    const angle = Math.atan2(-1, 1.3); // Çizginin açısı

    ctx.beginPath();
    ctx.moveTo(cx + size * 0.5, cy - size * 0.5);
    ctx.lineTo(
        cx + size * 0.5 - arrowSize * Math.cos(angle - Math.PI / 6),
        cy - size * 0.5 - arrowSize * Math.sin(angle - Math.PI / 6)
    );
    ctx.moveTo(cx + size * 0.5, cy - size * 0.5);
    ctx.lineTo(
        cx + size * 0.5 - arrowSize * Math.cos(angle + Math.PI / 6),
        cy - size * 0.5 - arrowSize * Math.sin(angle + Math.PI / 6)
    );
    ctx.stroke();
}

/**
 * Toplam Kaçıklık (Total Runout) - Çift ok
 */
function drawTotalRunout(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    r: number,
    cfg: SymbolDrawConfig
): void {
    const size = r * 0.6;
    const gap = r * 0.15;

    // İlk çizgi ve ok
    ctx.beginPath();
    ctx.moveTo(cx - size * 0.8 - gap, cy + size * 0.5 + gap);
    ctx.lineTo(cx + size * 0.5 - gap, cy - size * 0.5 + gap);
    ctx.stroke();

    // İkinci çizgi ve ok
    ctx.beginPath();
    ctx.moveTo(cx - size * 0.8 + gap, cy + size * 0.5 - gap);
    ctx.lineTo(cx + size * 0.5 + gap, cy - size * 0.5 - gap);
    ctx.stroke();

    // Ok başları
    const arrowSize = r * 0.2;
    const angle = Math.atan2(-1, 1.3);

    // İlk ok
    ctx.beginPath();
    ctx.moveTo(cx + size * 0.5 - gap, cy - size * 0.5 + gap);
    ctx.lineTo(
        cx + size * 0.5 - gap - arrowSize * Math.cos(angle - Math.PI / 6),
        cy - size * 0.5 + gap - arrowSize * Math.sin(angle - Math.PI / 6)
    );
    ctx.moveTo(cx + size * 0.5 - gap, cy - size * 0.5 + gap);
    ctx.lineTo(
        cx + size * 0.5 - gap - arrowSize * Math.cos(angle + Math.PI / 6),
        cy - size * 0.5 + gap - arrowSize * Math.sin(angle + Math.PI / 6)
    );
    ctx.stroke();

    // İkinci ok
    ctx.beginPath();
    ctx.moveTo(cx + size * 0.5 + gap, cy - size * 0.5 - gap);
    ctx.lineTo(
        cx + size * 0.5 + gap - arrowSize * Math.cos(angle - Math.PI / 6),
        cy - size * 0.5 - gap - arrowSize * Math.sin(angle - Math.PI / 6)
    );
    ctx.moveTo(cx + size * 0.5 + gap, cy - size * 0.5 - gap);
    ctx.lineTo(
        cx + size * 0.5 + gap - arrowSize * Math.cos(angle + Math.PI / 6),
        cy - size * 0.5 - gap - arrowSize * Math.sin(angle + Math.PI / 6)
    );
    ctx.stroke();
}

// ====================================================================
// MATERIAL CONDITION SEMBOLLERI
// ====================================================================

/**
 * Material Condition sembolü çiz
 */
export function drawMaterialCondition(
    ctx: CanvasRenderingContext2D,
    condition: MaterialCondition,
    x: number,
    y: number,
    config: Partial<SymbolDrawConfig> = {}
): void {
    const cfg = { ...DEFAULT_DRAW_CONFIG, ...config };
    applyContextSettings(ctx, cfg);

    const size = cfg.size;
    const cx = x + size / 2;
    const cy = y + size / 2;
    const r = (size - cfg.padding * 2) / 2;

    if (condition === MaterialCondition.RFS || condition === MaterialCondition.RMB) {
        // RFS - No symbol (ASME Y14.5-2018)
        return;
    }

    // Daire çiz
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.7, 0, Math.PI * 2);
    ctx.stroke();

    // Harf
    let letter = '';
    switch (condition) {
        case MaterialCondition.MMC:
        case MaterialCondition.MMB:
            letter = 'M';
            break;
        case MaterialCondition.LMC:
        case MaterialCondition.LMB:
            letter = 'L';
            break;
    }

    if (letter) {
        ctx.font = `bold ${r * 0.9}px Arial`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(letter, cx, cy);
    }
}

// ====================================================================
// TOLERANCE ZONE MODİFİERLERİ
// ====================================================================

/**
 * Tolerance Zone Modifier sembolü çiz
 */
export function drawToleranceZoneModifier(
    ctx: CanvasRenderingContext2D,
    modifier: ToleranceZoneModifier,
    x: number,
    y: number,
    config: Partial<SymbolDrawConfig> = {}
): void {
    const cfg = { ...DEFAULT_DRAW_CONFIG, ...config };
    applyContextSettings(ctx, cfg);

    const size = cfg.size;
    const cx = x + size / 2;
    const cy = y + size / 2;
    const r = (size - cfg.padding * 2) / 2;

    switch (modifier) {
        case ToleranceZoneModifier.DIAMETER:
            // ⌀ sembolü
            ctx.beginPath();
            ctx.arc(cx, cy, r * 0.5, 0, Math.PI * 2);
            ctx.stroke();
            // Çizgi
            ctx.beginPath();
            ctx.moveTo(cx - r * 0.7, cy + r * 0.5);
            ctx.lineTo(cx + r * 0.7, cy - r * 0.5);
            ctx.stroke();
            break;

        case ToleranceZoneModifier.SPHERICAL_DIAMETER:
            // S⌀
            ctx.font = `bold ${r * 0.6}px Arial`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText('S', x + cfg.padding, cy);

            // Diameter sembolü
            ctx.beginPath();
            ctx.arc(cx + r * 0.3, cy, r * 0.35, 0, Math.PI * 2);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(cx, cy + r * 0.3);
            ctx.lineTo(cx + r * 0.6, cy - r * 0.3);
            ctx.stroke();
            break;

        case ToleranceZoneModifier.RADIUS:
            ctx.font = `bold ${r}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('R', cx, cy);
            break;

        case ToleranceZoneModifier.SPHERICAL_RADIUS:
            ctx.font = `bold ${r * 0.8}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('SR', cx, cy);
            break;

        case ToleranceZoneModifier.PROJECTED:
        case ToleranceZoneModifier.FREE_STATE:
        case ToleranceZoneModifier.TANGENT_PLANE:
        case ToleranceZoneModifier.UNEQUALLY_DISPOSED:
            // Daire içinde harf
            ctx.beginPath();
            ctx.arc(cx, cy, r * 0.7, 0, Math.PI * 2);
            ctx.stroke();

            const letters: Record<ToleranceZoneModifier, string> = {
                [ToleranceZoneModifier.PROJECTED]: 'P',
                [ToleranceZoneModifier.FREE_STATE]: 'F',
                [ToleranceZoneModifier.TANGENT_PLANE]: 'T',
                [ToleranceZoneModifier.UNEQUALLY_DISPOSED]: 'U',
                [ToleranceZoneModifier.STATISTICAL]: 'ST',
                [ToleranceZoneModifier.NONE]: '',
                [ToleranceZoneModifier.DIAMETER]: '',
                [ToleranceZoneModifier.SPHERICAL_DIAMETER]: '',
                [ToleranceZoneModifier.RADIUS]: '',
                [ToleranceZoneModifier.SPHERICAL_RADIUS]: ''
            };

            ctx.font = `bold ${r * 0.8}px Arial`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(letters[modifier], cx, cy);
            break;

        case ToleranceZoneModifier.NONE:
        default:
            break;
    }
}

// ====================================================================
// DATUM SEMBOLLERİ
// ====================================================================

/**
 * Datum Feature sembolü çiz (kare çerçeve + harf)
 */
export function drawDatumFeature(
    ctx: CanvasRenderingContext2D,
    letter: string,
    x: number,
    y: number,
    config: Partial<SymbolDrawConfig> = {}
): void {
    const cfg = { ...DEFAULT_DRAW_CONFIG, ...config };
    applyContextSettings(ctx, cfg);

    const size = cfg.size;
    const padding = cfg.padding || 2;

    // Kare çerçeve
    ctx.beginPath();
    ctx.rect(x + padding, y + padding, size - padding * 2, size - padding * 2);
    ctx.stroke();

    // Sol kenar referans çizgisi (datum indicator)
    const triangleSize = size * 0.15;
    ctx.beginPath();
    ctx.moveTo(x + padding, y + size / 2 - triangleSize);
    ctx.lineTo(x - triangleSize, y + size / 2);
    ctx.lineTo(x + padding, y + size / 2 + triangleSize);
    ctx.closePath();
    ctx.fill();

    // Harf
    ctx.font = `bold ${size * 0.6}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(letter.toUpperCase(), x + size / 2, y + size / 2);
}

/**
 * Datum Target sembolü çiz (daire + çarpı)
 */
export function drawDatumTarget(
    ctx: CanvasRenderingContext2D,
    targetId: string,
    x: number,
    y: number,
    config: Partial<SymbolDrawConfig> = {}
): void {
    const cfg = { ...DEFAULT_DRAW_CONFIG, ...config };
    applyContextSettings(ctx, cfg);

    const size = cfg.size;
    const cx = x + size / 2;
    const cy = y + size / 2;
    const r = (size - (cfg.padding || 2) * 2) / 2;

    // Daire
    ctx.beginPath();
    ctx.arc(cx, cy, r * 0.8, 0, Math.PI * 2);
    ctx.stroke();

    // Yatay kesme çizgisi
    ctx.beginPath();
    ctx.moveTo(cx - r * 0.8, cy);
    ctx.lineTo(cx + r * 0.8, cy);
    ctx.stroke();

    // Target ID (alt yarıda)
    ctx.font = `bold ${r * 0.6}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(targetId, cx, cy + r * 0.4);
}

// ====================================================================
// FEATURE CONTROL FRAME ÇİZİMİ
// ====================================================================

/**
 * Feature Control Frame çerçeve genişliğini hesapla
 */
export function calculateFCFWidth(
    ctx: CanvasRenderingContext2D,
    text: string,
    fontSize: number,
    cellPadding: number = 8
): number {
    ctx.font = `bold ${fontSize}px Arial`;
    return ctx.measureText(text).width + cellPadding * 2;
}

/**
 * FCF hücre çiz
 */
export function drawFCFCell(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    content: string,
    config: Partial<SymbolDrawConfig> = {}
): void {
    const cfg = { ...DEFAULT_DRAW_CONFIG, ...config };
    applyContextSettings(ctx, cfg);

    // Çerçeve
    ctx.strokeRect(x, y, width, height);

    // İçerik
    ctx.font = `bold ${height * 0.6}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(content, x + width / 2, y + height / 2);
}

/**
 * FCF sembol hücresi çiz (GD&T sembolü için)
 */
export function drawFCFSymbolCell(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    characteristic: GDTCharacteristicType,
    config: Partial<SymbolDrawConfig> = {}
): void {
    const cfg = { ...DEFAULT_DRAW_CONFIG, ...config };
    applyContextSettings(ctx, cfg);

    // Çerçeve
    ctx.strokeRect(x, y, width, height);

    // Sembol
    const symbolSize = Math.min(width, height) * 0.8;
    drawGDTSymbol(
        ctx,
        characteristic,
        x + (width - symbolSize) / 2,
        y + (height - symbolSize) / 2,
        { ...cfg, size: symbolSize }
    );
}

// ====================================================================
// YARDIMCI FONKSİYONLAR
// ====================================================================

/**
 * Canvas oluştur ve sembol çiz
 */
export function createSymbolCanvas(
    type: GDTCharacteristicType,
    size: number = 32,
    color: string = '#FFFFFF'
): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    const ctx = createCleanContext(canvas, size, size, 2);

    drawGDTSymbol(ctx, type, 0, 0, { size, color, lineWidth: 2 });

    return canvas;
}

/**
 * Tüm sembollerin önizleme canvas'ını oluştur
 */
export function createAllSymbolsPreview(
    size: number = 32,
    color: string = '#FFFFFF',
    columns: number = 5
): HTMLCanvasElement {
    const types = Object.values(GDTCharacteristicType);
    const rows = Math.ceil(types.length / columns);

    const canvas = document.createElement('canvas');
    const totalWidth = size * columns;
    const totalHeight = size * rows;
    const ctx = createCleanContext(canvas, totalWidth, totalHeight, 2);

    types.forEach((type, index) => {
        const col = index % columns;
        const row = Math.floor(index / columns);
        drawGDTSymbol(ctx, type, col * size, row * size, { size, color, lineWidth: 2 });
    });

    return canvas;
}
